import { useSelector, useDispatch } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

import { PageLoader, PageLoaderWrapper } from 'shared-components/Loaders';
import { useGetCurrentParticipantQuery } from 'store/api/pkd/endpoints/user';
import { useGetPersonAttributesQuery } from 'store/api/pkd/endpoints/person';
import { getPersonMedicalRecords } from 'pages/MedicalInformation/utils';
import { getPersonContactRecords } from 'pages/ContactInformation/utils';
import { PersonStatus } from 'utils/enum';
import { setIntendedRoute } from 'store/application';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import { useLogout } from 'hooks/useLogout';
import { ErrorMessages } from 'utils/string';
import Swal from 'sweetalert2';

const PrivateRoute = ({ children, redirectPath = '/sign-in' }) => {
  const intendedRoute = useSelector((state) => state.application.intendedRoute);
  const { isAuthenticated, isLoading, error } = useAuth0();

  const { pathname } = useLocation();
  const dispatch = useDispatch();

  if (error) {
    Swal.fire({
      title             : 'Unauthorized!',
      html              : ErrorMessages[error?.message] ?? error?.message,
      icon              : 'error',
      confirmButtonText : 'SIGN IN'
    }).then(function (result){
      if (result.isConfirmed || result.dismiss === 'backdrop') {
        return window.location.href = redirectPath;
      }
    });
    return (
      <PageLoaderWrapper>
        <PageLoader />
      </PageLoaderWrapper>
    );
  }

  if (isLoading) {
    return (
      <PageLoaderWrapper>
        <PageLoader />
      </PageLoaderWrapper>
    );
  }

  if (!isAuthenticated) {
    dispatch(setIntendedRoute(pathname));
    return <Navigate
      to={redirectPath} replace
    />;
  }

  if (intendedRoute && intendedRoute !== pathname) {
    return <Navigate
      to={intendedRoute} replace
    />;
  }
  dispatch(setIntendedRoute(null));
  return children ? children : <Outlet />;
};

export default PrivateRoute;

export const VerifiedEmailRoute = ({ children }) => {
  const { user } = useAuth0();

  if (user?.email_verified) {
    return <Navigate
      to={'/medical-information'} replace
    />;
  }

  return children ? children : <Outlet />;
};

export const CompletedSecondaryOverviewRoute = ({ children }) => {
  const { user } = useAuth0();

  if (!user?.email_verified) {
    return <Navigate
      to={'/verify-email'} replace
    />;
  }

  return children ? children : <Outlet />;
};

export const CompletedConsentRoute = ({ children }) => {
  const { user, isLoading: auth0IsLoading } = useAuth0();
  const { data: person, isLoading: isPersonLoading } =
    useGetCurrentParticipantQuery();
  const {
    personAttributes = [],
    isPersonAttributesLoading,
    isPersonAttributesFetching,
  } = useGetPersonAttributesQuery(
    { personId: person?.id },
    {
      skip             : isPersonLoading || auth0IsLoading,
      selectFromResult : ({ data, isFetching, isLoading }) => ({
        personAttributes : data?.data?.map((attribute) => ({
          ...attribute.attributes,
          id : attribute.id,
        })),
        isPersonAttributesLoading  : isLoading,
        isPersonAttributesFetching : isFetching,
      }),
    }
  );

  if (
    isPersonAttributesLoading ||
    isPersonLoading ||
    isPersonAttributesFetching
  ) {
    return (
      <PageLoaderWrapper>
        <PageLoader />
      </PageLoaderWrapper>
    );
  }

  const hasCompletedMedicalInfo =
    personAttributes.find(
      (attributeDetails) => attributeDetails.attribute === 'pkdDiagnosis'
    ) ||
    person?.included?.find(
      (attributeDetails) =>
        attributeDetails.data.attributes.name === 'pkdDiagnosis' &&
        attributeDetails.data.attributes.value !== ''
    );
  const navigation = !user?.email_verified
    ? '/verify-email'
    : !hasCompletedMedicalInfo
      ? '/medical-information'
      : '';
  if (navigation) {
    return <Navigate
      to={navigation} replace
    />;
  }

  return children ? children : <Outlet />;
};

export const DashboardProtectedRoute = ({ children }) => {
  const { handleLogout } = useLogout();

  const {
    auth0User,
    person,
    isNewUser: isUnauthorized,
    isPersonLoading,
  } = useGetPersonAndAttributes();
  // logout if authorized
  if (isUnauthorized) {
    return handleLogout();
  }

  // show verification page
  if (!auth0User?.email_verified)
    return <Navigate
      to={'/verify-email'} replace
    />;

  // show loading
  if (isPersonLoading) {
    return (
      <PageLoaderWrapper>
        <PageLoader />
      </PageLoaderWrapper>
    );
  }

  const hasFullyMigrated =
    Object.values(
      getPersonMedicalRecords(person?.attributes, {
        hasMigratedMedicalInfo : '',
      })
    ).length === 1;

  if (
    !hasFullyMigrated &&
    person?.state?.status !== PersonStatus.Initialized &&
    person?.state?.status !== PersonStatus.Authenticated
  ) {
    return <Navigate
      to={'/welcome'} replace
    />;
  }
  const hasCompletedMedicalInfo =
    Object.values(
      getPersonMedicalRecords(person?.attributes, {
        pkdDiagnosis : '',
      })
    ).length === 1;
  if (!hasCompletedMedicalInfo) {
    return <Navigate
      to={'/medical-information'} replace
    />;
  }

  const hasCompletedContactInfo =
    Object.values(getPersonContactRecords(person?.attributes, true)).length >=
    4;
  if (!hasCompletedContactInfo)
    return <Navigate
      to={'/contact-information'} replace
    />;

  if (
    person?.state?.status !== PersonStatus.Onboarded &&
    person?.state?.status !== PersonStatus.Enrolled &&
    person?.state?.status !== PersonStatus.Consented
  ) {
    return <Navigate
      to={'/consent'} replace
    />;
  }

  return children ? children : <Outlet />;
};
